import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
  TextareaAutosize
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Image,
  ArrowUpward,
  ArrowDownward,
  CloudUpload,
  Close
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { downloadService } from '../../services/downloadService';

const DownloadManagement = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDownload, setEditingDownload] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    order_position: 1,
    is_active: true
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const queryClient = useQueryClient();

  // Buscar downloads
  const { data: downloadsData, isLoading } = useQuery({
    queryKey: ['downloads'],
    queryFn: () => downloadService.getAllDownloads(),
  });

  const downloads = downloadsData?.data?.downloads || [];

  // Mutations
  const createDownloadMutation = useMutation({
    mutationFn: (data) => downloadService.createDownload(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['downloads']);
      setSnackbar({ open: true, message: 'Download criado com sucesso!', severity: 'success' });
      handleCloseDialog();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao criar download', severity: 'error' });
    }
  });

  const updateDownloadMutation = useMutation({
    mutationFn: ({ id, data }) => downloadService.updateDownload(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['downloads']);
      setSnackbar({ open: true, message: 'Download atualizado com sucesso!', severity: 'success' });
      handleCloseDialog();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao atualizar download', severity: 'error' });
    }
  });

  const deleteDownloadMutation = useMutation({
    mutationFn: (id) => downloadService.deleteDownload(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['downloads']);
      setSnackbar({ open: true, message: 'Download deletado com sucesso!', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao deletar download', severity: 'error' });
    }
  });

  const reorderDownloadMutation = useMutation({
    mutationFn: ({ id, order }) => downloadService.reorderDownload(id, order),
    onSuccess: () => {
      queryClient.invalidateQueries(['downloads']);
      setSnackbar({ open: true, message: 'Ordem do download atualizada!', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao reordenar download', severity: 'error' });
    }
  });

  const handleOpenDialog = (download = null) => {
    if (download) {
      setEditingDownload(download);
      setFormData({
        title: download.title || '',
        description: download.description || '',
        image_url: download.image_url || '',
        order_position: download.order_position || 1,
        is_active: download.is_active !== undefined ? download.is_active : true
      });
      setPreviewUrl(download.image_url || '');
    } else {
      setEditingDownload(null);
      setFormData({
        title: '',
        description: '',
        image_url: '',
        order_position: downloads.length + 1,
        is_active: true
      });
      setPreviewUrl('');
    }
    setSelectedFile(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingDownload(null);
    setFormData({
      title: '',
      description: '',
      image_url: '',
      order_position: 1,
      is_active: true
    });
    setSelectedFile(null);
    setPreviewUrl('');
  };

  const handleSubmit = () => {
    let dataToSend = formData;
    
    // Se há um arquivo selecionado, criar FormData
    if (selectedFile) {
      const formDataToSend = new FormData();
      formDataToSend.append('download_image', selectedFile);
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('is_active', formData.is_active);
      formDataToSend.append('order_position', formData.order_position);
      dataToSend = formDataToSend;
    }
    
    if (editingDownload) {
      updateDownloadMutation.mutate({ id: editingDownload.id, data: dataToSend });
    } else {
      createDownloadMutation.mutate(dataToSend);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Tem certeza que deseja deletar este download?')) {
      deleteDownloadMutation.mutate(id);
    }
  };

  const handleReorder = (id, direction) => {
    const download = downloads.find(d => d.id === id);
    if (!download) return;

    const newOrder = direction === 'up' ? download.order_position - 1 : download.order_position + 1;
    reorderDownloadMutation.mutate({ id, order: newOrder });
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        setSnackbar({ open: true, message: 'Por favor, selecione apenas arquivos de imagem', severity: 'error' });
        return;
      }
      
      // Validar tamanho do arquivo (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setSnackbar({ open: true, message: 'O arquivo deve ter no máximo 5MB', severity: 'error' });
        return;
      }
      
      setSelectedFile(file);
      
      // Criar preview da imagem
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // Limpar campo de URL da imagem quando um arquivo é selecionado
      setFormData({ ...formData, image_url: '' });
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl('');
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'error';
  };

  const getStatusLabel = (isActive) => {
    return isActive ? 'Ativo' : 'Inativo';
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress sx={{ color: '#FF6B35' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" sx={{ fontFamily: 'Orbitron', fontWeight: 700, color: '#FF6B35' }}>
              Gerenciar Downloads
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                bgcolor: '#FF6B35',
                '&:hover': { bgcolor: '#FF8A65' }
              }}
            >
              Adicionar Download
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ bgcolor: 'rgba(10, 10, 10, 0.5)' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Imagem</TableCell>
                  <TableCell>Título</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Ordem</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {downloads
                  .sort((a, b) => a.order_position - b.order_position)
                  .map((download, index) => (
                  <TableRow key={download.id || `download-${index}`}>
                    <TableCell>
                      {download.image_url ? (
                        <Avatar
                          src={download.image_url}
                          variant="rounded"
                          sx={{ width: 80, height: 50 }}
                        >
                          <Image />
                        </Avatar>
                      ) : (
                        <Avatar variant="rounded" sx={{ width: 80, height: 50, bgcolor: '#FF6B35' }}>
                          <Image />
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {download.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        {download.description?.substring(0, 50)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2">
                          {download.order_position}
                        </Typography>
                        <Box>
                          {download.order_position > 1 ? (
                            <Tooltip title="Mover para cima">
                              <IconButton
                                size="small"
                                onClick={() => handleReorder(download.id, 'up')}
                                sx={{ color: '#00D4FF' }}
                              >
                                <ArrowUpward fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <IconButton
                              size="small"
                              disabled
                              sx={{ color: 'rgba(255, 255, 255, 0.3)' }}
                            >
                              <ArrowUpward fontSize="small" />
                            </IconButton>
                          )}
                          {download.order_position < downloads.length ? (
                            <Tooltip title="Mover para baixo">
                              <IconButton
                                size="small"
                                onClick={() => handleReorder(download.id, 'down')}
                                sx={{ color: '#00D4FF' }}
                              >
                                <ArrowDownward fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <IconButton
                              size="small"
                              disabled
                              sx={{ color: 'rgba(255, 255, 255, 0.3)' }}
                            >
                              <ArrowDownward fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(download.is_active)}
                        color={getStatusColor(download.is_active)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(download)}
                            sx={{ color: '#00D4FF' }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Deletar">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(download.id)}
                            sx={{ color: '#FF1744' }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog para Editar/Criar Download */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingDownload ? 'Editar Download' : 'Adicionar Download'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Título do Download"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              margin="normal"
            />
            
            {/* Upload de Arquivo */}
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#FF6B35' }}>
                Imagem do Download (opcional)
              </Typography>
              
              {/* Preview da Imagem */}
              {previewUrl && (
                <Box sx={{ mb: 2, position: 'relative', display: 'inline-block' }}>
                  <img
                    src={previewUrl}
                    alt="Preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '120px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '2px solid #FF6B35'
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleRemoveFile}
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      bgcolor: '#FF1744',
                      color: 'white',
                      '&:hover': { bgcolor: '#D50000' }
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              )}
              
              {/* Botão de Upload */}
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="download-upload"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="download-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  sx={{
                    borderColor: '#FF6B35',
                    color: '#FF6B35',
                    '&:hover': {
                      borderColor: '#FF8A65',
                      bgcolor: 'rgba(255, 107, 53, 0.1)'
                    }
                  }}
                >
                  {selectedFile ? 'Alterar Imagem' : 'Selecionar Imagem'}
                </Button>
              </label>
              
              <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
                Formatos aceitos: JPG, PNG, GIF. Tamanho máximo: 5MB
              </Typography>
            </Box>
            
            {/* Campo de URL (alternativa ao upload) */}
            <TextField
              fullWidth
              label="OU URL da Imagem"
              value={formData.image_url}
              onChange={(e) => {
                setFormData({ ...formData, image_url: e.target.value });
                if (e.target.value) {
                  setSelectedFile(null);
                  setPreviewUrl(e.target.value);
                }
              }}
              margin="normal"
              placeholder="https://exemplo.com/imagem.jpg"
              disabled={!!selectedFile}
              helperText={selectedFile ? "Desmarque o arquivo selecionado para usar URL" : "Use este campo se preferir inserir uma URL"}
            />
            
            <TextField
              fullWidth
              label="Descrição do Download"
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              placeholder="Descreva o download. Links na descrição serão automaticamente clicáveis."
            />
            
            <TextField
              fullWidth
              label="Posição na Ordem"
              type="number"
              value={formData.order_position}
              onChange={(e) => setFormData({ ...formData, order_position: parseInt(e.target.value) || 1 })}
              margin="normal"
              inputProps={{ min: 1, max: downloads.length + 1 }}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Download Ativo"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createDownloadMutation.isPending || updateDownloadMutation.isPending}
            sx={{ bgcolor: '#FF6B35', '&:hover': { bgcolor: '#FF8A65' } }}
          >
            {(createDownloadMutation.isPending || updateDownloadMutation.isPending) ? 
              <CircularProgress size={20} /> : 'Salvar'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DownloadManagement;
